import { useSecurityContext } from '@components/Navigation/SecurityContext'
import {
  GraviButton,
  GraviGrid,
  Horizontal,
  TextCell,
  Texto,
  useGridFetch,
  validateFloat,
  validateNotEmptyString,
} from '@gravitate-js/excalibrr'
import api from '@utils/api'
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { ProductRowExpand } from './Configuration/ProductRowExpand'
import { productCreateConfig } from './createConfig'
import { useUserSettings } from '@hooks/settings'
import { Cascader, Drawer } from 'antd'
import { ManageLidCodes } from '@components/Admin/Product/ManageLidCodes/ManageLidCodes'
import { useTankLidCodes } from '@api/useTankLidCodes'
import { LidCodeForm } from '@components/Admin/Product/ManageLidCodes/LidCodeForm'
import { PlusOutlined } from '@ant-design/icons'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'products'

export function Product() {
  const [metadata, setMetadata] = useState()
  const [products, setProducts] = useState()
  const [manageLidCodes, setManageLidCodes] = useState(false)
  const [activeDrawerKey, setActiveDrawerKey] = useState()
  const [addingNewItem, setAddingNewItem] = useState(false)
  const externalRef = useRef()
  const { rowData, refresh } = useGridFetch(api.getProducts)
  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.site_write
  const { updateSetting, getSetting } = useUserSettings()
  const { useLidCodeOverview } = useTankLidCodes()
  const { data: lidCodes, isPending: loadingLidCodes } = useLidCodeOverview()
  const makeItemLabel = (item) => {
    if (!item) return null
    return {
      value: item._id,
      label: (
        <Horizontal className={''} verticalCenter>
          <img alt={'image'} src={item.image_url} style={{ width: '25px' }} />
          <Texto className={'ml-2'}>{item.name}</Texto>
        </Horizontal>
      ),
      name: item.name,
    }
  }
  useEffect(() => {
    api.getProductMetadata().then((resp) => {
      setMetadata(resp.data)
    })
    api.getProductNames().then((resp) => {
      setProducts(resp.data.sort())
    })
    getSetting('manage-tank-lid-code-active-tab').then((resp) => {
      resp && setActiveDrawerKey(resp)
    })
  }, [])
  const lidCodeOptions = useMemo(() => {
    if (!lidCodes) return []
    const defaults = lidCodes.filter((item) => item.is_system_default).sort((a, b) => a.name.localeCompare(b.name))
    const custom = lidCodes.filter((item) => !item.is_system_default).sort((a, b) => a.name.localeCompare(b.name))

    return [
      {
        label: 'System Defaults',
        value: 'system_defaults',
        name: 'System Defaults',
        children: defaults.map(makeItemLabel),
      },
      {
        label: 'Custom',
        value: 'custom',
        name: 'Custom',
        children: custom.map(makeItemLabel),
      },
    ]
  }, [lidCodes])
  const columnDefs = useMemo(
    () => (metadata ? getColumnDefs(metadata, lidCodeOptions, lidCodes) : []),
    [metadata, lidCodeOptions, lidCodes]
  )
  const getRowId = useCallback((row) => row.data?.id, [])
  const agPropOverrides = useMemo(
    () => ({ getRowId, frameworkComponents: { productRowExpand: ProductRowExpand } }),
    [getRowId]
  )

  return (
    <>
      <GraviGrid
        controlBarProps={{
          title: 'Product Management',
          actionButtons: (
            <>
              <UploadDownloadButtons
                downloadEP={api.downloadProducts}
                uploadEP={canEdit && api.uploadProducts}
                fileName='Products.xlsx'
                refreshCallback={refresh}
              />
              <GraviButton
                className='mr-4'
                buttonText='Manage Lid Codes'
                color='primary'
                appearance='outlined'
                onClick={() => setManageLidCodes(true)}
              />
            </>
          ),
        }}
        agPropOverrides={agPropOverrides}
        columnDefs={columnDefs}
        storageKey={STORAGE_KEY}
        rowData={rowData}
        updateEP={canEdit && api.updateProduct}
        createConfig={productCreateConfig}
        createSelectOptions={metadata}
        createEP={canEdit && api.createProduct}
        masterDetail
        detailCellRenderer='productRowExpand'
        shouldInsertCreated
        suppressCellSelection
        externalRef={externalRef}
        detailCellRendererParams={{
          metadata,
          products,
          refresh,
          gridAPIRef: externalRef,
        }}
        onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
        {...gridViewManager(STORAGE_KEY, updateSetting)}
      />
      <Drawer
        title={'Manage Lid Codes'}
        open={manageLidCodes}
        onClose={() => setManageLidCodes(false)}
        placement={'right'}
        destroyOnClose
        width={600}
        footer={
          activeDrawerKey === '2' && (
            <div>
              {addingNewItem ? (
                <LidCodeForm handleClose={() => setAddingNewItem(false)} />
              ) : (
                <Horizontal verticalCenter horizontalCenter className={'m-2'}>
                  <GraviButton
                    theme1
                    style={{ borderRadius: '5px' }}
                    onClick={() => setAddingNewItem(true)}
                    buttonText={'Add Lid Code'}
                    icon={<PlusOutlined />}
                  />
                </Horizontal>
              )}
            </div>
          )
        }
      >
        <ManageLidCodes
          lidCodes={lidCodes}
          loading={loadingLidCodes}
          activeKey={activeDrawerKey}
          setActiveKey={setActiveDrawerKey}
          addingNewItem={addingNewItem}
          setAddingNewItem={setAddingNewItem}
        />
      </Drawer>
    </>
  )
}
const getColumnDefs = (metaData, lidCodesOptions, lidCodes) => [
  {
    field: 'id',
    flex: 2,
    valueGetter: () => '',
    headerName: '',
    maxWidth: 50,
    cellRenderer: 'agGroupCellRenderer',
  },
  {
    field: 'name',
    filter: true,
    flex: 2,
    cellRenderer: ({ value }) => <TextCell value={value} weight='bold' />,
    valueSetter: validateNotEmptyString,
  },
  {
    field: 'short_name',
    headerName: 'Short Name',
    flex: 1.5,
    valueSetter: validateNotEmptyString,
  },
  {
    field: 'group',
    flex: 1.5,
    valueSetter: validateNotEmptyString,
    cellEditor: 'agSelectCellEditor',
    cellEditorParams: {
      values: metaData.groups.filter((group) => group !== 'BioFuels'),
    },
  },
  {
    headerName: 'Specific Gravity',
    field: 'specific_gravity',
    cellClass: 'right-align-cell',
    type: 'rightAligned',
    sortable: false,
    valueSetter: (params) => validateFloat(params, 0),
  },
  {
    editable: false,
    headerName: 'Brand',
    field: 'brand',
    flex: 1,
  },
  {
    editable: false,
    field: 'grade',
    headerName: 'Grade',
    flex: 1,
  },
  {
    editable: false,
    headerName: 'MF Rating',
    field: 'mf_rating',
    headerTooltip: 'Motor Fuel Rating',
    flex: 1,
  },
  {
    editable: false,
    headerName: 'Oxy Type',
    field: 'oxy_type',
    headerTooltip: 'Oxygenated Type',
    flex: 1,
  },
  {
    editable: false,
    headerName: 'Oxy Content',
    field: 'oxy_content',
    headerTooltip: 'Oxygenated Content',
    flex: 1,
  },
  {
    editable: false,
    headerName: 'Weight Group',
    field: 'weight_group',
    flex: 1,
  },
  {
    headerName: 'Lid Code',
    field: 'tank_lid_code_id',
    cellEditor: LidCodeCellEditor,
    cellEditorParams: {
      lidCodes,
      lidCodesOptions,
    },
    minWidth: 250,
    cellRenderer: (params) => {
      const item = params?.data?.tank_lid_code_id
        ? lidCodes?.find((item) => item._id === params?.data?.tank_lid_code_id)
        : null
      if (!item) return <Texto>&mdash;</Texto>
      return (
        <Horizontal verticalCenter>
          <img alt={'image'} src={item.image_url} style={{ width: '25px' }} />
          <Texto className={'ml-2'}>{item.name}</Texto>
        </Horizontal>
      )
    },
    valueGetter: (params) => {
      return params?.data?.tank_lid_code_id
        ? lidCodes?.find((item) => item._id === params?.data?.tank_lid_code_id)?.name
        : ''
    },
    comparator: (valueA, valueB) => valueB?.localeCompare(valueA),
  },
  {
    field: 'icon',
    sortable: false,
    editable: true,
  },
]

export const LidCodeCellEditor = forwardRef((props, ref) => {
  const [value, setValue] = useState('')
  const refInput = useRef(null)

  useImperativeHandle(ref, () => {
    return {
      getValue() {
        return value
      },
      isPopup() {
        return true
      },
      isCancelBeforeStart() {
        return false
      },
      isCancelAfterEnd() {
        return false
      },
    }
  })

  const tagRender = (renderProps) => {
    const item = props.lidCodes?.find((item) => item._id === renderProps?.[0])
    if (!item) return null
    return (
      <Horizontal verticalCenter>
        <img alt={'image'} src={item.image_url} style={{ width: '25px' }} />
        <Texto className={'ml-2'}>{item.name}</Texto>
      </Horizontal>
    )
  }
  useEffect(() => {
    setValue(props.value)
  }, [])

  const filter = (inputValue, path) =>
    path.some((option) => option.name.toLowerCase().indexOf(inputValue.toLowerCase()) > -1)

  return (
    <Horizontal alignItems='center'>
      <Cascader
        ref={refInput}
        options={props.lidCodesOptions ?? []}
        showSearch={{ filter }}
        showCheckedStrategy={Cascader.SHOW_CHILD}
        value={[value]}
        onChange={(v) => {
          if (!v) setValue('')
          if (v) setValue(v?.[1])
        }}
        displayRender={tagRender}
        expandTrigger='hover'
        placeholder='Search Lid Codes'
        allowClear
      />
    </Horizontal>
  )
})
